import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const planId = searchParams.get('plan') || 'starter';
  
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const maxPolls = 10;

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  // Polling para verificar status do pagamento
  useEffect(() => {
    if (status === 'checking' && pollCount < maxPolls) {
      const timer = setTimeout(() => {
        checkPaymentStatus();
        setPollCount(prev => prev + 1);
      }, 2000);
      return () => clearTimeout(timer);
    } else if (pollCount >= maxPolls && status === 'checking') {
      // Timeout - mas vamos considerar sucesso se chegou nessa página
      handlePaymentSuccess();
    }
  }, [pollCount, status]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/payments/stripe-status/${sessionId}`);
      
      if (response.data.is_paid || response.data.payment_status === 'paid') {
        handlePaymentSuccess(response.data);
      } else if (response.data.status === 'complete') {
        handlePaymentSuccess(response.data);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      // Mesmo com erro, se estamos nessa página o Stripe provavelmente confirmou
      if (pollCount >= 3) {
        handlePaymentSuccess();
      }
    }
  };

  const handlePaymentSuccess = (data = null) => {
    setStatus('success');
    setPaymentData(data);
    
    // Atualizar localStorage do usuário
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    user.payment_status = 'paid';
    user.subscription_plan = planId;
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.removeItem('pending_payment');
    
    toast.success('Pagamento confirmado! 🎉');
  };

  const goToUpsell = () => {
    navigate(`/upsell?plan=${planId}&payment_id=${sessionId}`);
  };

  const goToDashboard = () => {
    // Forçar reload para garantir que o estado seja atualizado
    window.location.href = '/dashboard';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 text-center">
          
          {status === 'checking' && (
            <>
              <Loader2 className="h-16 w-16 text-blue-400 mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-bold text-white mb-2">Confirmando Pagamento...</h1>
              <p className="text-gray-400 mb-4">
                Aguarde enquanto verificamos seu pagamento.
              </p>
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-sm text-gray-500">
                  Verificação {pollCount + 1} de {maxPolls}
                </p>
                <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${((pollCount + 1) / maxPolls) * 100}%` }}
                  ></div>
                </div>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-20 w-20 bg-green-500/20 rounded-full animate-ping"></div>
                </div>
                <CheckCircle className="h-20 w-20 text-green-400 mx-auto mb-4 relative z-10" />
              </div>
              
              <h1 className="text-3xl font-bold text-white mb-2">Pagamento Confirmado! 🎉</h1>
              <p className="text-gray-400 mb-6">
                Seu plano foi ativado com sucesso. Você já tem acesso completo à plataforma!
              </p>
              
              <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 mb-6">
                <p className="text-green-400 font-semibold">
                  ✅ Plano {planId.charAt(0).toUpperCase() + planId.slice(1)} Ativo
                </p>
              </div>

              {/* Oferta especial */}
              <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-4 mb-6">
                <p className="text-yellow-400 font-semibold text-sm mb-1">🎁 OFERTA ESPECIAL!</p>
                <p className="text-white text-sm">
                  Faça upgrade agora e ganhe <span className="font-bold text-yellow-400">50% OFF</span>!
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={goToUpsell}
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-black py-4 rounded-lg font-bold text-lg hover:from-yellow-600 hover:to-orange-600 transition-all flex items-center justify-center gap-2"
                >
                  VER OFERTA ESPECIAL
                  <ArrowRight className="h-5 w-5" />
                </button>
                
                <button
                  onClick={goToDashboard}
                  className="w-full bg-white/10 text-white py-3 rounded-lg font-semibold hover:bg-white/20 transition-all"
                >
                  Ir para o Dashboard
                </button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2">Erro na Verificação</h1>
              <p className="text-gray-400 mb-6">
                Não foi possível confirmar seu pagamento. Se você completou o pagamento, 
                por favor aguarde alguns minutos e tente acessar novamente.
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={() => window.location.reload()}
                  className="w-full bg-blue-500 text-white py-3 rounded-lg font-semibold hover:bg-blue-600 transition-all"
                >
                  Tentar Novamente
                </button>
                
                <button
                  onClick={() => navigate('/checkout?plan=' + planId)}
                  className="w-full bg-white/10 text-white py-3 rounded-lg font-semibold hover:bg-white/20 transition-all"
                >
                  Voltar ao Checkout
                </button>
              </div>
            </>
          )}
        </div>

        {/* Suporte */}
        <div className="mt-6 text-center text-gray-400 text-sm">
          <p>Problemas? Entre em contato: suporte@monetizemaster.com</p>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
